import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import QRCode from 'react-qr-code';
import UserLayout from '../../components/user/UserLayout';
import toast from 'react-hot-toast';
import {
  Wallet, Upload, Camera, Clock,
  CheckCircle, XCircle, AlertCircle, DollarSign
} from 'lucide-react';

export default function WalletRechargePage() {
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [balance, setBalance] = useState(null);
  const [amount, setAmount] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [preview, setPreview] = useState(null);
  const [transactionId, setTransactionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState('UPI');
  const [selectedCryptoNetwork, setSelectedCryptoNetwork] = useState('TRC20');
  const fileRef = useRef();

  const availableCryptoNetworks = ['TRC20', 'BEP20', 'ERC20'].filter((network) => {
    const key = network.toLowerCase();
    return paymentConfig?.[`${key}Enabled`] && paymentConfig?.[`${key}Address`];
  });

  useEffect(() => {
    if (!paymentConfig) return;
    const hasUpi = paymentConfig.upiId && paymentConfig.upiEnabled !== false;
    const hasBank = paymentConfig.bankName && paymentConfig.bankEnabled !== false;
    const hasCrypto = availableCryptoNetworks.length > 0;

    if (selectedMode === 'UPI' && !hasUpi) {
      setSelectedMode(hasBank ? 'Bank' : (hasCrypto ? 'Crypto' : 'UPI'));
    }
    if (selectedMode === 'Bank' && !hasBank) {
      setSelectedMode(hasUpi ? 'UPI' : (hasCrypto ? 'Crypto' : 'Bank'));
    }
    if (selectedMode === 'Crypto' && !hasCrypto) {
      setSelectedMode(hasUpi ? 'UPI' : (hasBank ? 'Bank' : 'Crypto'));
    }
    if (hasCrypto && !availableCryptoNetworks.includes(selectedCryptoNetwork)) {
      setSelectedCryptoNetwork(availableCryptoNetworks[0]);
    }
  }, [paymentConfig, availableCryptoNetworks, selectedCryptoNetwork, selectedMode]);

  const fetchData = async () => {
    const [configRes, requestsRes, balanceRes] = await Promise.all([
      axios.get('/api/payment/admin-payment-config').catch(() => ({ data: {} })),
      axios.get('/api/wallet-request/my-requests').catch(() => ({ data: { requests: [] } })),
      axios.get('/api/wallet/balance').catch(() => ({ data: {} }))
    ]);
    setPaymentConfig(configRes.data);
    setMyRequests(requestsRes.data.requests || []);
    setBalance(balanceRes.data);
  };

  useEffect(() => { fetchData(); }, []);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Max 5MB allowed');
    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshot(reader.result);
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) < 100) return toast.error('Minimum $100');
    if (!screenshot) return toast.error('Payment screenshot upload submit');
    if (!transactionId || !transactionId.trim()) return toast.error('Please enter transaction ID');
    setLoading(true);
    try {
      await axios.post('/api/wallet-request/request', {
        amount: parseFloat(amount),
        screenshot,
        paymentMethod: selectedMode,
        transactionId: transactionId.trim(),
      });
      toast.success('Request submit ho gayi! Admin approve karega.');
      setAmount('');
      setScreenshot(null);
      setPreview(null);
      setTransactionId('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const hasPending = myRequests.some(r => r.status === 'pending');

  const statusBadge = {
    pending: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    approved: 'bg-primary-500/20 text-primary-300 border-primary-500/30',
    rejected: 'bg-red-500/20 text-red-300 border-red-500/30'
  };

  const getCryptoQrValue = (address, network) => {
    if (!address) return '';
    switch (network) {
      case 'TRC20':
        return `tron:${address}`;
      case 'BEP20':
        return `ethereum:${address}`;
      case 'ERC20':
        return `ethereum:${address}`;
      default:
        return address;
    }
  };

  const statusIcon = {
    pending: <Clock className="w-4 h-4 text-amber-400" />,
    approved: <CheckCircle className="w-4 h-4 text-primary-400" />,
    rejected: <XCircle className="w-4 h-4 text-red-400" />
  };

  return (
    <UserLayout>
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-white mb-2">Main Wallet Recharge</h1>
        <p className="text-gray-400 mb-6">
          Submit the payment and upload the screenshot. The admin will approve it.
        </p>

        {/* Current Balance */}
        <div className="bg-gradient-to-br from-primary-600/20 to-green-600/10 border border-primary-500/30 rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Main Wallet Balance</p>
              <p className="text-3xl font-bold text-white mt-1">
                ${(balance?.mainWallet || 0).toFixed(2)}
              </p>
              <div className={`flex items-center gap-1.5 mt-1 text-xs ${balance?.isActive ? 'text-primary-400' : 'text-red-400'}`}>
                <span className={`w-2 h-2 rounded-full ${balance?.isActive ? 'bg-primary-500' : 'bg-red-500'}`}></span>
                {balance?.isActive ? 'Account Active' : 'Account Inactive — Recharge is required'}
              </div>
            </div>
            <div className="w-14 h-14 bg-primary-500/20 rounded-2xl flex items-center justify-center">
              <DollarSign className="w-7 h-7 text-primary-400" />
            </div>
          </div>
        </div>

        {/* Pending request warning */}
        {hasPending && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <p className="text-amber-300 text-sm">
              A request is already pending. Please wait for admin approval.
            </p>
          </div>
        )}

        {!hasPending && (
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Payment Details */}
            <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-4">Payment Details</h3>

              <div className="flex flex-wrap gap-2 mb-4">
                {paymentConfig?.upiId && paymentConfig?.upiEnabled !== false && (
                  <button
                    type="button"
                    onClick={() => setSelectedMode('UPI')}
                    className={`rounded-2xl px-4 py-2 text-xs font-semibold ${selectedMode === 'UPI' ? 'bg-primary-500 text-white' : 'bg-dark-600 text-gray-300 hover:bg-dark-500'}`}
                  >
                    UPI
                  </button>
                )}
                {paymentConfig?.bankName && paymentConfig?.bankEnabled !== false && (
                  <button
                    type="button"
                    onClick={() => setSelectedMode('Bank')}
                    className={`rounded-2xl px-4 py-2 text-xs font-semibold ${selectedMode === 'Bank' ? 'bg-primary-500 text-white' : 'bg-dark-600 text-gray-300 hover:bg-dark-500'}`}
                  >
                    Bank
                  </button>
                )}
                {availableCryptoNetworks.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedMode('Crypto')}
                    className={`rounded-2xl px-4 py-2 text-xs font-semibold ${selectedMode === 'Crypto' ? 'bg-primary-500 text-white' : 'bg-dark-600 text-gray-300 hover:bg-dark-500'}`}
                  >
                    Crypto
                  </button>
                )}
              </div>

              {selectedMode === 'UPI' && paymentConfig?.upiId && paymentConfig?.upiEnabled !== false ? (
                <>
                  {paymentConfig.qrCodeImage && (
                    <div className="text-center mb-4">
                      <img
                        src={paymentConfig.qrCodeImage}
                        alt="QR Code"
                        className="w-44 h-44 mx-auto rounded-xl border border-white/10 object-contain bg-white p-2"
                      />
                      <p className="text-gray-400 text-xs mt-2">Scan to Pay</p>
                    </div>
                  )}
                  <div className="bg-dark-600 rounded-xl p-3 mb-3">
                    <p className="text-gray-500 text-xs mb-0.5">UPI ID</p>
                    <div className="flex items-center justify-between">
                      <p className="text-white font-mono font-semibold">{paymentConfig.upiId}</p>
                      <button onClick={() => {
                        navigator.clipboard.writeText(paymentConfig.upiId);
                        toast.success('UPI ID copied!');
                      }} className="text-primary-400 text-xs hover:text-primary-300">Copy</button>
                    </div>
                  </div>
                </>
              ) : selectedMode === 'Bank' && paymentConfig?.bankName && paymentConfig?.bankEnabled !== false ? (
                <div className="bg-dark-600 rounded-xl p-3 space-y-1.5 text-xs">
                  <p className="text-gray-500 font-medium mb-1">Bank Transfer</p>
                  {[
                    ['Account Name', paymentConfig.accountHolderName],
                    ['Bank', paymentConfig.bankName],
                    ['Account No.', paymentConfig.accountNumber],
                    ['IFSC', paymentConfig.ifscCode],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-gray-400">{k}</span>
                      <span className="text-white font-mono">{v}</span>
                    </div>
                  ))}
                </div>
              ) : selectedMode === 'Crypto' && availableCryptoNetworks.length > 0 ? (
                <>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {availableCryptoNetworks.map((network) => (
                      <button
                        key={network}
                        type="button"
                        onClick={() => setSelectedCryptoNetwork(network)}
                        className={`rounded-2xl px-3 py-2 text-xs font-semibold ${selectedCryptoNetwork === network ? 'bg-primary-500 text-white' : 'bg-dark-600 text-gray-300 hover:bg-dark-500'}`}
                      >
                        {network}
                      </button>
                    ))}
                  </div>
                  {(() => {
                    const address = paymentConfig?.[`${selectedCryptoNetwork.toLowerCase()}Address`];
                    return (
                      <div className="bg-dark-600 rounded-2xl p-4 mb-4">
                        <p className="text-gray-400 text-xs mb-3 font-medium">{selectedCryptoNetwork} Address</p>
                        <div className="text-center mb-4">
                          <div className="inline-block rounded-2xl bg-dark-700 p-3">
                            <QRCode
                              value={getCryptoQrValue(address, selectedCryptoNetwork)}
                              size={156}
                              bgColor="#111827"
                              fgColor="#ffffff"
                            />
                          </div>
                          <p className="text-gray-400 text-xs mt-2">Scan to Pay via {selectedCryptoNetwork}</p>
                        </div>
                        <div className="bg-dark-700 rounded-xl p-3 text-xs">
                          <div className="flex justify-between mb-2">
                            <span className="text-gray-400">Network</span>
                            <span className="text-white">{selectedCryptoNetwork}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Address</span>
                            <span className="text-white font-mono break-all text-right">{address}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="text-center py-6 text-gray-500 text-sm">
                  The selected payment method is not available. Please choose another option or contact admin.
                </div>
              )}

              {paymentConfig?.instructions && (
                <p className="text-gray-500 text-xs mt-3">{paymentConfig.instructions}</p>
              )}
            </div>

            {/* Upload Form */}
            <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-4">Upload Payment Proof</h3>

              {/* Amount */}
              <div className="mb-4">
                <label className="block text-xs text-gray-400 mb-1.5">Amount ($)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    className="w-full bg-dark-600 border border-white/10 text-white rounded-xl pl-8 pr-4 py-3 focus:outline-none focus:border-primary-500"
                    placeholder="Min $100"
                    min="100"
                  />
                </div>
                <div className="flex gap-2 mt-2">
                  {[500, 1000, 2000, 5000].map(a => (
                    <button key={a} onClick={() => setAmount(String(a))}
                      className="px-3 py-1 bg-dark-600 border border-white/10 text-gray-300 hover:text-white rounded-lg text-xs transition-colors">
                      ${a}
                    </button>
                  ))}
                </div>
              </div>

              {/* Screenshot Upload */}
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-white/20 hover:border-primary-500/50 rounded-xl p-5 text-center cursor-pointer transition-colors mb-4"
              >
                {preview ? (
                  <img src={preview} alt="Screenshot" className="max-h-40 mx-auto rounded-lg object-contain" />
                ) : (
                  <>
                    <Camera className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">Click to upload screenshot</p>
                    <p className="text-gray-600 text-xs mt-1">JPG, PNG up to 5MB</p>
                  </>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />

              {preview && (
                <button onClick={() => { setScreenshot(null); setPreview(null); }}
                  className="w-full text-gray-400 hover:text-white text-sm border border-white/10 rounded-xl py-2 mb-3 transition-colors">
                  Remove & Re-upload
                </button>
              )}

              <div className="mb-4">
                <label className="block text-xs text-gray-400 mb-1.5">Transaction ID</label>
                <input
                  type="text"
                  value={transactionId}
                  onChange={e => setTransactionId(e.target.value)}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500"
                  placeholder="Enter payment transaction ID"
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={loading || !amount || !screenshot}
                className="w-full bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {loading
                  ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  : <><Upload className="w-4 h-4" /> Submit Recharge Request</>}
              </button>
            </div>
          </div>
        )}

        {/* Request History */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h3 className="text-white font-semibold">Recharge History</h3>
          </div>
          <div className="divide-y divide-white/5">
            {myRequests.length === 0 ? (
              <div className="py-10 text-center">
                <Wallet className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No recharge requests found yet</p>
              </div>
            ) : myRequests.map(r => (
              <div key={r._id} className="px-4 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-dark-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      {statusIcon[r.status]}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">${r.amount.toFixed(2)}</p>
                      <p className="text-gray-500 text-xs">
                        {new Date(r.createdAt).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                      {r.transactionId && <p className="text-green-400 text-xs">Txn ID: {r.transactionId}</p>}
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border capitalize ${statusBadge[r.status]}`}>
                    {r.status}
                  </span>
                </div>
                {r.adminNote && (
                  <p className="text-gray-500 text-xs mt-2 ml-12">Note: {r.adminNote}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </UserLayout>
  );
}
